// Dnešní trasa — „Noční kokpit": prsten postupu + slovní stavy, sbalené
// dřívější zastávky, zlatě zvýrazněná aktuální, „N× po okně!" pill,
// TRANK/materiál úkoly, ukotvené CTA na další zastávku.
// Logika (route/today cache, progress store, storno ack) beze změny.

import { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { RouteForToday } from "@routera/shared";
import { stopKey } from "@routera/shared";
import { IonContent, IonPage, IonSpinner } from "@ionic/react";
import { api, materialApi } from "../lib/api";
import { saveRoute, loadCachedRoutes, clearCachedRoutes } from "../lib/db";
import { useSyncStore } from "../store/syncStore";
import { useShiftStore } from "../store/shiftStore";
import { useStopProgressStore } from "../store/stopProgressStore";
import { useStornoAckStore } from "../store/stornoAckStore";
import { StopRow } from "../components/StopRow";
import { NetworkBadge } from "../components/NetworkBadge";
import { RefreshButton } from "../components/RefreshButton";
import { ThemeToggle } from "../components/ThemeToggle";
import { pauseMinutes, shiftHhMm } from "../lib/pauseEta";

const hhmmToMin = (v?: string): number | null => {
  const m = /^(\d{1,2}):(\d{2})$/.exec((v ?? "").trim());
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};

export function TodayRouteScreen() {
  const history = useHistory();
  const location = useLocation();
  const rid = new URLSearchParams(location.search).get("rid");
  const [showEarlier, setShowEarlier] = useState(false);
  const progress = useStopProgressStore((s) => s.progress);
  const hydrateFromBackend = useStopProgressStore((s) => s.hydrateFromBackend);
  const progressShiftId = useStopProgressStore((s) => s.shiftId);
  const hydrated = useStopProgressStore((s) => s.hydrated);

  const statusOf = (id: string) => progress[id]?.status ?? "waiting";
  const doneAtOf = (id: string) => {
    const iso = progress[id]?.arrivedAt;
    if (!iso) return undefined;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return undefined;
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  };
  const futileReasonOf = (id: string) => progress[id]?.futileReason;

  const shift = useShiftStore((s) => s.shift);
  const online = useSyncStore((s) => s.online);
  const isAcked = useStornoAckStore((s) => s.isAcked);
  const ack = useStornoAckStore((s) => s.ack);
  void ack; // storno se odklikává v detailu; tady jen čteme

  // Hydrate progress při mountu aktivní směny (refresh PWA tabu nuloval state)
  useEffect(() => {
    if (shift?.id && (progressShiftId !== shift.id || !hydrated) && online) {
      void hydrateFromBackend(shift.id);
    }
  }, [shift?.id, progressShiftId, hydrated, online, hydrateFromBackend]);

  const { data: routes = [], isLoading } = useQuery<RouteForToday[]>({
    queryKey: ["mobile", "route", "today"],
    queryFn: async () => {
      try {
        const fresh = await api.route.today();
        await clearCachedRoutes();
        for (const r of fresh) await saveRoute(r);
        return fresh;
      } catch (e) {
        const cached = await loadCachedRoutes();
        if (cached.length > 0) return cached;
        throw e;
      }
    },
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
  });

  const route = rid ? (routes.find((r) => r.route_id === rid) ?? routes[0]) : routes[0];
  const ridParam = rid ? `?rid=${rid}` : route ? `?rid=${route.route_id}` : "";
  const pauseMin = shift && !shift.end_ts ? pauseMinutes(shift.id) : 0;

  // Převzetí materiálu z počítárny = PRVNÍ úkol trasy (jen dokud je co nakládat;
  // hotový úkol mizí jako TRANK úkoly).
  // Kolik pro mě na depu leží k naložení. Pool odpovídá podle mých dnešních
  // zastávek — dřív se četly zásilky, které mi někdo přiřadil v noci.
  const { data: kNalozeni } = useQuery({
    queryKey: ["mobile", "material", "k-nalozeni"],
    queryFn: materialApi.kNalozeni,
    enabled: !!shift && !shift.end_ts,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });
  const matToLoad = kNalozeni?.sacky ?? [];

  // ── Sestavení seznamu (zachovaná sémantika původní obrazovky) ─────────────
  type Item =
    | { kind: "stop"; stop: any; idx: number; id: string; status: string; closed: boolean }
    | { kind: "depot"; closed: boolean }
    | { kind: "mat"; closed: boolean };

  let items: Item[] = [];
  let total = 0, doneCount = 0, futileCount = 0, lateCount = 0;

  if (route) {
    const hasDepot = route.depot_lat != null && route.depot_lng != null;
    const depotStatus = statusOf("depot");

    const stopItems: Item[] = route.stops
      .map((raw, idx) => {
        const s = raw as any;
        const id: string = s.collection ? `col-${s.collection_id ?? idx}` : (s.stop_key ?? stopKey(s));
        const st = s.collection ? (s.done ? "done" : "waiting") : statusOf(id);
        const closed = st === "done" || st === "futile" || (!!s.storno && isAcked(id));
        return { kind: "stop" as const, stop: s, idx, id, status: st, closed };
      })
      // Hotové TRANK úkoly mizí úplně (úkol, ne pobočka) — do metrik se počítají níž.
      .filter((it) => !((it.stop as any).collection && (it.stop as any).done));

    items = [...stopItems];
    if (matToLoad.length > 0) {
      items.unshift({ kind: "mat", closed: false });
    }
    if (hasDepot) {
      const depotPos = (route as any).depot_position;
      const depotItem: Item = { kind: "depot", closed: depotStatus === "done" };
      if (typeof depotPos === "number") {
        const at = items.filter((it) => it.kind === "stop" && it.idx < depotPos).length;
        items.splice(at, 0, depotItem);
      } else {
        items.push(depotItem);
      }
    }

    // Návrat na základnu se do počtu zastávek NEpočítá — kolečko i „zbývá"
    // ukazují jen skutečné zastávky/úkoly, depo je patka trasy.
    const activeStops = route.stops.filter((s: any) => !s.storno);
    total = activeStops.length;
    for (const s of activeStops as any[]) {
      if (s.collection) { if (s.done) doneCount++; continue; }
      const id: string = s.stop_key ?? stopKey(s);
      const st = statusOf(id);
      if (st === "done") doneCount++;
      if (st === "futile") futileCount++;
      if (st !== "done" && st !== "futile" && s.eta && s.window_to) {
        const e = hhmmToMin(shiftHhMm(String(s.eta), pauseMin));
        const b = hhmmToMin(String(s.window_to));
        if (e != null && b != null && e > b) lateCount++;
      }
    }
  }

  const closedItems = items.filter((it) => it.closed);
  const openItems = items.filter((it) => !it.closed);
  const closedTotal = doneCount + futileCount;
  const remaining = Math.max(0, total - closedTotal);
  // „Hotovo" pro CTA = nezbývá ŽÁDNÁ otevřená položka (vč. depa a úkolů);
  // počítadla nahoře ale depo ignorují.
  const allDone = total > 0 && openItems.length === 0;

  const openStopDetail = (it: Item) => {
    if (it.kind === "mat") {
      history.push("/shift/material");
      return;
    }
    if (it.kind === "depot") {
      history.push(`/route/depot${ridParam}`);
      return;
    }
    const s = it.stop;
    const kind = String((s as Record<string, unknown>).trank_kind ?? "");
    if (s.collection && s.collection_id) {
      if (kind === "material_nalozit" || kind === "material_vylozit") {
        const rezim = kind === "material_vylozit" ? "vylozit" : "nalozit";
        const sep = ridParam ? "&" : "?";
        history.push(`/route/trank-material/${s.collection_id}${ridParam}${sep}kind=${rezim}`);
      } else {
        history.push(`/route/collection/${s.collection_id}${ridParam}`);
      }
    } else {
      history.push(`/route/stop/${it.idx}${ridParam}`);
    }
  };

  const renderItem = (it: Item, current: boolean) => {
    if (it.kind === "mat") {
      const seals = matToLoad.map((m) => m.pecet);
      return (
        <StopRow
          key="mat"
          idx={null}
          name="Převzít materiál"
          address={`počítárna${seals.length ? ` · ${seals.join(" · ")}` : ""}`}
          stopStatus={"waiting" as any}
          current={current}
          task
          onClick={() => history.push("/shift/material")}
        />
      );
    }
    if (it.kind === "depot") {
      const st = statusOf("depot");
      // Jakmile je návrat NA ŘADĚ, povýší se z tiché patky na plnohodnotný
      // zlatě zvýrazněný bod trasy; jinak zůstává nenápadnou patkou seznamu.
      if (current) {
        return (
          <button key="depot" className="stop now tsk" onClick={() => openStopDetail(it)}>
            <span className="rail">
              <svg viewBox="0 0 24 24"><path d="M3 11l9-7 9 7" /><path d="M5.5 9.5V20h13V9.5" /></svg>
            </span>
            <span className="main">
              <span className="nm">C.I.T {String(route?.depot ?? "").toUpperCase()}<span className="chip-ukol">návrat</span></span>
              <span className="ad">Návrat na základnu · odevzdání svozu</span>
            </span>
            <span className="win">
              {st === "arrived"
                ? <span className="st-ok">na místě</span>
                : st === "started"
                  ? <span className="st-warn">jedu tam</span>
                  : <span>na řadě</span>}
            </span>
          </button>
        );
      }
      return (
        <button key="depot" className="depotfoot" onClick={() => openStopDetail(it)} style={{ width: "100%" }}>
          <svg viewBox="0 0 24 24"><path d="M3 11l9-7 9 7" /><path d="M5.5 9.5V20h13V9.5" /></svg>
          Návrat na základnu · C.I.T {route?.depot ?? ""}
          {it.closed && <span style={{ color: "var(--ok)" }}>· hotovo</span>}
        </button>
      );
    }
    const s = it.stop;
    const isTask = !!s.collection;
    const eta = s.eta && !it.closed ? shiftHhMm(String(s.eta), pauseMin) : undefined;
    return (
      <StopRow
        key={it.id}
        idx={isTask ? null : it.idx + 1}
        name={s.name ?? (isTask ? "Úkol" : "Pobočka")}
        address={s.address}
        client={s.client}
        branchId={isTask ? undefined : (s.branch_id ?? undefined)}
        windowFrom={s.window_from}
        windowTo={s.window_to}
        stopStatus={it.status as any}
        current={current}
        task={isTask}
        doneAt={doneAtOf(it.id)}
        futileReason={futileReasonOf(it.id)}
        storno={!!s.storno}
        stornoAcked={!!s.storno && isAcked(it.id)}
        note={s.note ?? undefined}
        temp={!!s.temp}
        eta={eta}
        onClick={() => openStopDetail(it)}
      />
    );
  };

  // Aktuální = první otevřená ne-storno položka (úkol i zastávka).
  const currentItem = openItems.find((it) => it.kind !== "stop" || !it.stop.storno);

  return (
    <IonPage>
      <IonContent scrollY={false}>
        <div className="ck scenebg" style={{ height: "100%" }}>
          <div className="top">
            <button className="back" onClick={() => history.push("/")}>‹</button>
            <span className="wm">ROUTERA <em>DRIVER</em></span>
            {shift && !shift.end_ts && <span className="live"><span className="dot" />živě</span>}
            <RefreshButton />
            <NetworkBadge />
            <ThemeToggle />
          </div>

          {isLoading ? (
            <div style={{ textAlign: "center", padding: 60 }}><IonSpinner name="crescent" /></div>
          ) : !route ? (
            <div className="pad-x">
              <h1>Žádná trasa pro dnešek</h1>
              <div className="h-sub">Dispečer ti zatím nepřiřadil trasu. Zkus to za chvíli, nebo mu zavolej.</div>
            </div>
          ) : (
            <>
              <div className="routehead">
                <div className="ring">
                  <svg width="86" height="86" viewBox="0 0 86 86">
                    <circle className="track" cx="43" cy="43" r="38" fill="none" strokeWidth="7" />
                    <circle
                      cx="43" cy="43" r="38" fill="none" stroke="var(--brass)" strokeWidth="7"
                      strokeLinecap="round"
                      strokeDasharray={2 * Math.PI * 38}
                      strokeDashoffset={2 * Math.PI * 38 * (1 - (total > 0 ? closedTotal / total : 0))}
                    />
                  </svg>
                  <div className="num">{closedTotal}/{total}</div>
                </div>
                <div className="meta" style={{ minWidth: 0 }}>
                  <b>{route.name ?? "Dnešní trasa"}</b>
                  <span>{route.depot ? `depo ${route.depot}` : "dnešní kurz"}</span>
                  {lateCount > 0 && (
                    <span className="latepill">{lateCount}× po okně!</span>
                  )}
                </div>
                <div className="eta-chip">
                  {remaining === 0 && total > 0
                    ? <><b style={{ color: "var(--ok)" }}>✓</b><span>zastávky hotovo</span></>
                    : <><b>{remaining}</b><span>{remaining === 1 ? "zbývá zastávka" : "zbývá zastávek"}</span></>}
                </div>
              </div>

              {route.notes && (
                <div className="pad-x" style={{ paddingBottom: 4 }}>
                  <div style={{ fontSize: 12.5, color: "var(--brass)", fontStyle: "italic" }}>{route.notes}</div>
                </div>
              )}

              <div className="stops">
                {closedItems.length > 0 && (
                  <button className="earlier" onClick={() => setShowEarlier((v) => !v)}>
                    {showEarlier
                      ? "Skrýt dřívější zastávky"
                      : `Zobrazit dřívější · ${closedItems.length} ${closedItems.length === 1 ? "položka" : closedItems.length < 5 ? "položky" : "položek"}`}
                  </button>
                )}
                {showEarlier && closedItems.map((it) => renderItem(it, false))}
                {openItems.map((it) => renderItem(it, it === currentItem))}

                {allDone && (
                  <div className="card" style={{ textAlign: "center", borderColor: "rgba(61,214,132,.45)" }}>
                    <div style={{ fontSize: 20, color: "var(--ok)", fontWeight: 800 }}>✓ Vše hotovo</div>
                    <div style={{ fontSize: 12.5, color: "var(--dim)", marginTop: 4 }}>Ukonči trasu tlačítkem dole.</div>
                  </div>
                )}
              </div>

              {!shift?.end_ts && (
                <div className="bottom">
                  {allDone ? (
                    <button className="cta" onClick={() => history.push(`/shift/end${rid ? `?rid=${rid}` : ""}`)}>
                      Ukončit {route.name ?? "trasu"}
                    </button>
                  ) : currentItem ? (
                    <>
                      <button
                        className="cta"
                        onClick={() => currentItem.kind === "mat" ? history.push("/shift/material") : openStopDetail(currentItem)}
                      >
                        {currentItem.kind === "mat"
                          ? "Úkol · Převzít materiál"
                          : currentItem.kind === "depot"
                            ? "Na základnu"
                            : currentItem.stop.collection
                              ? `Úkol · ${currentItem.stop.name ?? "TRANK"}`
                              : `Na zastávku · ${currentItem.stop.branch_id ? `${currentItem.stop.branch_id} ` : ""}${String(currentItem.stop.name ?? "").toUpperCase()}`}
                      </button>
                      <button
                        className="ghostbtn"
                        style={{ margin: "10px auto 0" }}
                        onClick={() => history.push(`/shift/end${rid ? `?rid=${rid}` : ""}`)}
                      >
                        Ukončit trasu
                      </button>
                    </>
                  ) : null}
                </div>
              )}
            </>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
}
